/**
 * `Transport` — the amp faceplate. `docs/design-direction.md` §9.4, §6.12, §8.1.
 *
 * A 56px `--faceplate` strip divided into cells by 1px rules — **a strip, not
 * floating circles** — with a screen-printed label under every control. That is the
 * cheapest and strongest device in the system for making the UI read as an object
 * rather than as a page, and it costs one span per control (§6.12).
 *
 * Play is the one exception to the strip: a 64px `--r-round` `--accent` plate with a
 * hard `--ink-offset` and an `--text-on-accent` glyph, overhanging the strip's top
 * edge. Glyphs on ink are the near-black, never the cream — cream on `--warm-fill` is
 * 2.8:1 and is banned outright (§12.9). Prev and next are 44×44 with 2px-stroke
 * geometry in `--text`.
 *
 * The secondary controls are `Chip`s, and the accent fill is applied here rather than
 * in the primitive, exactly as `Chip`'s own contract records: a filter chip and a
 * latched transport control are the same object with different ink (§9.7 inverts to
 * cream; §9.4 fills with `--accent`). Their printed names stay Latin — `RANDOM`,
 * `REPEAT`, `SPEED`, `VERSION` are silkscreen control vocabulary, which is the one
 * place §11 licenses English in a Russian app — and each is a real toggle, so each
 * carries `aria-pressed` truthfully:
 *
 *  · `RANDOM` — on/off.
 *  · `REPEAT` — pressed whenever repeat is not `off`; the mode is printed on the chip.
 *  · `SPEED` — pressed when the deck is off nominal 45, i.e. running at 33.
 *  · `VERSION` — pressed when the dub/instrumental cut is the one playing; disabled and
 *    `--text-3` when the catalogue has no version for this track, never hidden, so the
 *     strip does not reflow between tracks.
 *
 * The three icon-only controls carry Russian `aria-label`s (§9.4). Haptics are the
 * screen's: `impactOccurred('light')` on a press (§7.2 INKING) belongs to whoever owns
 * the interaction, not to a component that would become untestable by reaching for
 * `window.Telegram` itself — the same rule the primitives follow.
 */

import type { JSX } from 'react';

import styles from './components.module.css';
import { Chip } from './primitives/Chip';
import { Silkscreen } from './primitives/Silkscreen';
import { cx } from './primitives/Rule';

/** The two speeds a 7" is cut at. `45` is nominal; `33` is the half-speed play. */
export type TransportSpeed = 33 | 45;
export type RepeatMode = 'off' | 'all' | 'one';

export const PREV_LABEL = 'Предыдущий трек';
export const NEXT_LABEL = 'Следующий трек';
export const PLAY_LABEL = 'Играть';
export const PAUSE_LABEL = 'Пауза';

export interface TransportProps {
  readonly playing: boolean;
  readonly onPlayPause: () => void;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly shuffle: boolean;
  readonly onShuffle: () => void;
  readonly repeat: RepeatMode;
  readonly onRepeat: () => void;
  readonly speed: TransportSpeed;
  readonly onSpeed: () => void;
  /** `undefined` when the catalogue has no dub or instrumental cut for this track. */
  readonly onVersion?: (() => void) | undefined;
  readonly versionActive?: boolean;
  readonly className?: string | undefined;
}

export function Transport({
  playing,
  onPlayPause,
  onPrev,
  onNext,
  shuffle,
  onShuffle,
  repeat,
  onRepeat,
  speed,
  onSpeed,
  onVersion,
  versionActive = false,
  className,
}: TransportProps): JSX.Element {
  return (
    <div className={cx(styles.faceplateGroup, className)}>
      <div className={cx('faceplate', styles.transport)}>
        <span className={styles.cell}>
          <button type="button" className={styles.skip} aria-label={PREV_LABEL} onClick={onPrev}>
            <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
              <path
                d="M7 5v14M20 5L9 12l11 7z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <Silkscreen>PREV</Silkscreen>
        </span>

        <span className={styles.cell}>
          <button
            type="button"
            className={styles.play}
            aria-label={playing ? PAUSE_LABEL : PLAY_LABEL}
            aria-pressed={playing}
            onClick={onPlayPause}
          >
            <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false">
              {playing ? (
                <path d="M8 5h3v14H8zM13 5h3v14h-3z" fill="currentColor" />
              ) : (
                <path d="M8 5l11 7-11 7z" fill="currentColor" />
              )}
            </svg>
          </button>
          <Silkscreen>{playing ? 'PAUSE' : 'PLAY'}</Silkscreen>
        </span>

        <span className={styles.cell}>
          <button type="button" className={styles.skip} aria-label={NEXT_LABEL} onClick={onNext}>
            <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
              <path
                d="M17 5v14M4 5l11 7-11 7z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <Silkscreen>NEXT</Silkscreen>
        </span>
      </div>

      <div className={styles.secondary}>
        <Chip label="RANDOM" selected={shuffle} onToggle={onShuffle} className={styles.latch} />
        <Chip
          label={repeat === 'one' ? 'REPEAT 1' : 'REPEAT'}
          selected={repeat !== 'off'}
          onToggle={onRepeat}
          className={styles.latch}
        />
        <Chip
          label={`SPEED ${String(speed)}`}
          selected={speed !== 45}
          onToggle={onSpeed}
          className={styles.latch}
        />
        <Chip
          label="VERSION"
          selected={versionActive}
          disabled={onVersion === undefined}
          onToggle={onVersion ?? noop}
          className={styles.latch}
        />
      </div>
    </div>
  );
}

/** A disabled `<button>` never fires, so this is the shape of "no version catalogued". */
function noop(): void {
  /* deliberately empty */
}
